import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useLobbySocket } from '../hooks/useLobbySocket';
import { useSocket } from '../context/SocketContext';
import SetupModal from '../components/SetupModal.component';
import PlayerCard from '../components/PlayerCard.component';
import FortuneWheel from '../components/FortuneWheel.component';
import WinnerAnnouncement from '../components/WinnerAnnouncement.component';
import CountdownOverlay from '../components/CountdownOverlay.component';
import { BubbleBox } from '../components/BubbleBoxComponent';
import CartoonButton from '../components/CartoonButton.component';
import { CartoonIconButton } from '../components/CartoonButton.component';
import MinigameBar from '../components/minigame/MinigameBar.component';
import MinigameOverlay from '../components/minigame/MinigameOverlay.component';
import BuyMeACoffee from '../components/BuyMeACoffee.component';
import theme from '../theme';

// ─── Sub-components ────────────────────────────────────────────────────────

const LoadingScreen: React.FC = () => (
  <Box
    display="flex" flexDirection="column" alignItems="center"
    justifyContent="center" height="100vh" gap="16px"
    sx={{ background: theme.palette.background.default }}
  >
    <CircularProgress sx={{ color: 'white' }} size={64} />
    <Typography sx={{ color: 'white', fontFamily: '"Comic Sans MS", cursive, sans-serif', fontSize: '1.4rem', fontWeight: 'bold' }}>
      Joining lobby...
    </Typography>
  </Box>
);

const LobbyNotFoundScreen: React.FC<{ onGoHome: () => void }> = ({ onGoHome }) => (
  <Box
    display="flex" flexDirection="column" alignItems="center"
    justifyContent="center" height="100vh" gap="24px"
    sx={{ background: theme.palette.background.default }}
  >
    <Typography sx={{ color: 'white', fontFamily: '"Comic Sans MS", cursive, sans-serif', fontSize: '2rem', fontWeight: 'bold', textShadow: '2px 2px 0 rgba(0,0,0,0.3)' }}>
      Lobby not found 😢
    </Typography>
    <CartoonButton variant="contained" onClick={onGoHome} sx={{ background: '#ff4081' }}>
      Go Home
    </CartoonButton>
  </Box>
);

const LobbyHeader: React.FC<{ lobbyId: string; onLeave: () => void }> = ({ lobbyId, onLeave }) => {
  const [copied, setCopied] = React.useState(false);
  const [confirmLeave, setConfirmLeave] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <>
      <Box
        sx={{
          height: '64px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', px: 3,
          borderBottom: '3px solid rgba(0,0,0,0.2)',
          background: 'rgba(0,0,0,0.15)',
        }}
      >
        {/* Left: title + lobby ID + copy button + leave button */}
        <Box display="flex" alignItems="center" gap="8px" flexShrink={0}>
          <Typography sx={{ color: 'white', fontFamily: '"Comic Sans MS", cursive, sans-serif', fontWeight: 'bold', fontSize: '1.1rem', textShadow: '1px 1px 0 rgba(0,0,0,0.3)', whiteSpace: 'nowrap' }}>
            🎡 Who Pays? &nbsp;|&nbsp; Lobby: <strong>{lobbyId}</strong>
          </Typography>
          {copied && (
            <Typography variant="caption" sx={{ color: 'white', fontStyle: 'italic', whiteSpace: 'nowrap' }}>
              Copied!
            </Typography>
          )}
          <CartoonIconButton onClick={handleCopy} title="Copy invite link">
            <ContentCopyIcon sx={{ color: 'white', fontSize: '1.1rem' }} />
          </CartoonIconButton>
          <Box sx={{ ml: 1.5 }}>
            <CartoonButton
              variant="outlined"
              onClick={() => setConfirmLeave(true)}
              sx={{
                color: 'white', borderColor: 'rgba(255,255,255,0.5)',
                fontSize: '0.8rem', py: 0.5, px: 1.5,
                '&:hover': { borderColor: 'white', background: 'rgba(255,255,255,0.1)' },
              }}
            >
              Leave
            </CartoonButton>
          </Box>
        </Box>

        {/* Right: BMC */}
        <Box display="flex" alignItems="center" flexShrink={0}>
          <BuyMeACoffee />
        </Box>
      </Box>

      {/* Leave confirmation dialog */}
      <Dialog
        open={confirmLeave}
        onClose={() => setConfirmLeave(false)}
        PaperProps={{
          sx: {
            borderRadius: '20px', border: '3px solid black',
            boxShadow: '0 6px 0 black, 0 8px 20px rgba(0,0,0,0.3)', p: 1,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: '"Comic Sans MS", cursive, sans-serif', fontWeight: 'bold', fontSize: '1.3rem' }}>
          Leave the game? 👋
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: '"Comic Sans MS", cursive, sans-serif', fontSize: '0.95rem' }}>
            You'll be taken back to the main menu.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ gap: 1, pb: 2, px: 2 }}>
          <CartoonButton variant="outlined" onClick={() => setConfirmLeave(false)} sx={{ flex: 1 }}>
            Stay
          </CartoonButton>
          <CartoonButton
            variant="contained"
            onClick={() => { setConfirmLeave(false); onLeave(); }}
            sx={{ flex: 1, background: '#e53935' }}
          >
            Leave
          </CartoonButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const EmptyLobbyMessage: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap="16px">
      <BubbleBox
        onClick={isAdmin ? handleCopy : undefined}
        sx={isAdmin ? { cursor: 'pointer', userSelect: 'none', '&:hover': { filter: 'brightness(1.05)' } } : undefined}
      >
        <Typography sx={{ fontFamily: '"Comic Sans MS", cursive, sans-serif', fontWeight: 'bold' }}>
          {isAdmin
            ? copied ? '✅ Link copied!' : 'Share the lobby link to invite friends! 🎡'
            : 'Waiting for players to set up... 🎡'}
        </Typography>
      </BubbleBox>
    </Box>
  );
};

// ─── LobbyPage ─────────────────────────────────────────────────────────────

const LobbyPage: React.FC = () => {
  const { lobbyId } = useParams<{ lobbyId: string }>();
  const navigate = useNavigate();
  const { sessionToken } = useSocket();

  const {
    lobby, error, currentPlayer, isAdmin,
    setupPlayer, updateCoefficient, startSpin, resetGame, leaveLobby,
    launchMinigame, submitMinigameAnswer, closeMinigameResults,
  } = useLobbySocket(lobbyId ?? '', sessionToken);

  const handleLeave = React.useCallback(() => {
    leaveLobby();
    navigate('/');
  }, [leaveLobby, navigate]);

  if (error?.code === 'LOBBY_NOT_FOUND') {
    return <LobbyNotFoundScreen onGoHome={() => navigate('/')} />;
  }

  if (!lobby) return <LoadingScreen />;

  const needsSetup = !currentPlayer?.name;
  const readyPlayers = lobby.players.filter(p => p.name);
  const takenAvatars = lobby.players
    .filter(p => p.id !== sessionToken && p.avatarType === 'bundled' && p.avatar)
    .map(p => p.avatar);

  const minigameActive = !!lobby.minigameState;
  const showMinigameBar = isAdmin && lobby.phase === 'waiting' && readyPlayers.length >= 2 && !minigameActive;

  return (
    <Box sx={{ background: theme.palette.background.default, height: '100vh', overflow: 'hidden', width: '100%' }}>
      {needsSetup && (
        <SetupModal onSetup={setupPlayer} lobbyId={lobbyId ?? ''} takenAvatars={takenAvatars} />
      )}

      <LobbyHeader lobbyId={lobbyId ?? ''} onLeave={handleLeave} />

      <Box display="flex" height="calc(100vh - 64px)">
        {/* Player sidebar */}
        <Box
          sx={{
            width: 240, minWidth: 240, p: 2, overflowY: 'auto',
            borderRight: '3px solid rgba(0,0,0,0.15)',
            background: 'rgba(0,0,0,0.1)',
          }}
        >
          <Typography sx={{ color: 'white', fontFamily: '"Comic Sans MS", cursive, sans-serif', fontWeight: 'bold', fontSize: '0.9rem', mb: 1, textShadow: '1px 1px 0 rgba(0,0,0,0.2)' }}>
            Players ({readyPlayers.length})
          </Typography>
          {readyPlayers.map(player => (
            <PlayerCard
              key={player.id}
              player={player}
              isCurrentPlayer={player.id === sessionToken}
              isAdminViewing={isAdmin}
              onCoefficientChange={isAdmin ? updateCoefficient : undefined}
            />
          ))}
        </Box>

        {/* Wheel section */}
        <Box flex={1} display="flex" flexDirection="column" justifyContent="center" alignItems="center" gap={2}>
          {showMinigameBar && (
            <MinigameBar onLaunch={launchMinigame} />
          )}

          {readyPlayers.length < 2 ? (
            <EmptyLobbyMessage isAdmin={isAdmin} />
          ) : (
            <FortuneWheel
              players={readyPlayers}
              phase={lobby.phase}
              spinConfig={lobby.spinConfig}
              isAdmin={isAdmin}
              onSpinClick={startSpin}
              onResetClick={resetGame}
              winner={lobby.winner}
              currentPlayerId={sessionToken ?? undefined}
              adminId={lobby.adminId}
              minigameActive={minigameActive}
            />
          )}
        </Box>
      </Box>

      {/* Countdown overlay */}
      {lobby.phase === 'countdown' && <CountdownOverlay />}

      {/* Winner announcement */}
      {lobby.phase === 'result' && lobby.winner && (
        <WinnerAnnouncement
          winner={lobby.winner}
          isAdmin={isAdmin}
          onSpinAgain={isAdmin ? resetGame : undefined}
          onGoHome={() => { leaveLobby(); navigate('/'); }}
        />
      )}

      {/* Minigame overlay */}
      {lobby.minigameState && (
        <MinigameOverlay
          minigame={lobby.minigameState}
          players={readyPlayers}
          currentPlayerId={sessionToken ?? ''}
          isAdmin={isAdmin}
          onSubmit={submitMinigameAnswer}
          onCloseResults={closeMinigameResults}
        />
      )}
    </Box>
  );
};

export default LobbyPage;
